/**
 * Generated streaming hooks for NDJSON endpoints
 * Do not edit manually.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@/shared/auth/hooks/use-auth';
import { isE2ETestMode } from '@/shared/auth/utils/e2e';
import { captureMessage, Sentry } from '@/modules/sentry';
import { parseNDJSONStream } from '@/shared/utils/ndjson-parser';
import {
  DEFAULT_STREAM_LONG_REQUEST_THRESHOLD_MS,
  DEFAULT_STREAM_MAX_RETRIES,
  DEFAULT_STREAM_RETRY_BASE_DELAY_MS,
  DEFAULT_STREAM_TIMEOUT_MS,
  getExponentialBackoffDelayMs,
  isRetryableStreamError,
  isStreamAbortError,
  isStreamTimeoutError,
  STREAM_TIMEOUT_REASON,
  useStreamingWithCleanup,
} from '@/shared/hooks/useStreamingWithCleanup';
import { getTraceContext } from '@/shared/utils/trace-context';
import type {
  ThreadServiceStreamThreadMessagesBody,
  V1ThreadMessage,
  ThreadServiceResumeMessageStreamBody,
  ThreadServiceSendMessageBody,
} from '../models';

const waitForRetryDelay = async (delayMs: number): Promise<void> => {
  await new Promise<void>(resolve => {
    setTimeout(() => resolve(), delayMs);
  });
};

/** Threshold in ms above which token acquisition is reported as slow */
const SLOW_TOKEN_THRESHOLD_MS = 5_000;

interface useStreamingStreamThreadMessagesOptions {
  onMessage?: (event: V1ThreadMessage) => void;
  onComplete?: (events: V1ThreadMessage[]) => void;
  onError?: (error: Error) => void;
  timeoutMs?: number;
  maxRetries?: number;
  retryBaseDelayMs?: number;
  longRequestThresholdMs?: number;
  onRetry?: (details: {
    attempt: number;
    maxRetries: number;
    delayMs: number;
    reason: 'timeout' | 'retryable-error';
  }) => void;
  onLongRequest?: (details: { thresholdMs: number }) => void;
}

interface useStreamingResumeMessageStreamOptions {
  onMessage?: (event: V1ThreadMessage) => void;
  onComplete?: (events: V1ThreadMessage[]) => void;
  onError?: (error: Error) => void;
  timeoutMs?: number;
  maxRetries?: number;
  retryBaseDelayMs?: number;
  longRequestThresholdMs?: number;
  onRetry?: (details: {
    attempt: number;
    maxRetries: number;
    delayMs: number;
    reason: 'timeout' | 'retryable-error';
  }) => void;
  onLongRequest?: (details: { thresholdMs: number }) => void;
}

interface useStreamingSendMessageOptions {
  onMessage?: (event: V1ThreadMessage) => void;
  onComplete?: (events: V1ThreadMessage[]) => void;
  onError?: (error: Error) => void;
  timeoutMs?: number;
  maxRetries?: number;
  retryBaseDelayMs?: number;
  longRequestThresholdMs?: number;
  onRetry?: (details: {
    attempt: number;
    maxRetries: number;
    delayMs: number;
    reason: 'timeout' | 'retryable-error';
  }) => void;
  onLongRequest?: (details: { thresholdMs: number }) => void;
}

/**
 * @summary Stream thread messages in real-time as they are generated.
Deprecated: Use ResumeMessageStream instead for reconnection scenarios.
 */
export const useStreamingStreamThreadMessages = (
  options?: useStreamingStreamThreadMessagesOptions,
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLongRequest, setIsLongRequest] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { getAccessTokenSilently } = useAuth();
  const { createAbortController, cancelCurrentStream } =
    useStreamingWithCleanup();
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const streamThreadMessages = useCallback(
    async (
      organization: string,
      workspace: string,
      automation: string,
      agent: string,
      thread: string,
      data: ThreadServiceStreamThreadMessagesBody,
    ) => {
      setIsLoading(true);
      setIsLongRequest(false);
      setError(null);
      const streamLogPrefix = '[stream:zen/StreamThreadMessages]';
      const timeoutMs =
        optionsRef.current?.timeoutMs ?? DEFAULT_STREAM_TIMEOUT_MS;
      const maxRetries = Math.max(
        1,
        optionsRef.current?.maxRetries ?? DEFAULT_STREAM_MAX_RETRIES,
      );
      const retryBaseDelayMs =
        optionsRef.current?.retryBaseDelayMs ??
        DEFAULT_STREAM_RETRY_BASE_DELAY_MS;
      const longRequestThresholdMs =
        optionsRef.current?.longRequestThresholdMs ??
        DEFAULT_STREAM_LONG_REQUEST_THRESHOLD_MS;
      const longRequestTimer =
        longRequestThresholdMs > 0
          ? setTimeout(() => {
              setIsLongRequest(true);
              optionsRef.current?.onLongRequest?.({
                thresholdMs: longRequestThresholdMs,
              });
            }, longRequestThresholdMs)
          : null;

      try {
        let lastError: Error | null = null;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          const abortController = createAbortController();
          let didTimeout = false;
          // Track which phase the request is in for timeout diagnostics
          let phase: 'init' | 'request' | 'streaming' = 'init';
          let timeoutId: ReturnType<typeof setTimeout> | undefined;
          let messageCount = 0;
          const traceContext = getTraceContext();

          try {
            Sentry.logger.info(
              `${streamLogPrefix} attempt ${attempt}/${maxRetries}`,
              { timeoutMs, requestId: traceContext.requestId },
            );
            // Check for E2E test mode (SSR-safe: checks build-time VITE_E2E_TEST_MODE first,
            // then runtime window.__ENV__ if available)
            const isE2EMode = isE2ETestMode();
            const headers: Record<string, string> = {
              'Content-Type': 'application/json',
              Accept: 'application/x-ndjson',
            };
            if (isE2EMode) {
              headers['X-E2E-Test-Mode'] = 'true';
            } else {
              const tokenStart = Date.now();
              const token = await getAccessTokenSilently();
              const tokenDurationMs = Date.now() - tokenStart;
              if (tokenDurationMs > SLOW_TOKEN_THRESHOLD_MS) {
                captureMessage('slow_token_acquisition', {
                  level: 'warning',
                  tags: {
                    service: 'zen',
                    operation: 'StreamThreadMessages',
                  },
                  extra: { durationMs: tokenDurationMs },
                });
              }
              headers.Authorization = `Bearer ${token}`;
            }

            headers['x-kog-request-id'] = traceContext.requestId;

            // Start inactivity timeout AFTER token acquisition so auth latency
            // does not eat into the server response budget.
            // Rolling timeout: reset on every received message (including heartbeats).
            // Only fires if the server goes completely silent for timeoutMs.
            phase = 'request';
            const requestStartTime = Date.now();
            Sentry.logger.info(`${streamLogPrefix} sending request`, {
              url: `/api/v1/organizations/${organization}/workspaces/${workspace}/automations/${automation}/agents/${agent}/threads/${thread}/messages:stream`,
              requestId: traceContext.requestId,
            });
            timeoutId = setTimeout(() => {
              didTimeout = true;
              abortController.abort(STREAM_TIMEOUT_REASON);
            }, timeoutMs);

            const response = await fetch(
              `/api/v1/organizations/${organization}/workspaces/${workspace}/automations/${automation}/agents/${agent}/threads/${thread}/messages:stream`,
              {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
                signal: abortController.signal,
              },
            );

            const responseLatencyMs = Date.now() - requestStartTime;
            Sentry.logger.info(`${streamLogPrefix} response received`, {
              status: response.status,
              latencyMs: responseLatencyMs,
              requestId: traceContext.requestId,
            });

            if (!response.ok) {
              const httpError = new Error(
                `HTTP error! status: ${response.status}`,
              );
              (httpError as Error & { status: number }).status =
                response.status;
              throw httpError;
            }

            const reader = response.body?.getReader();
            if (!reader) throw new Error('No response body');

            phase = 'streaming';
            Sentry.logger.info(`${streamLogPrefix} streaming started`, {
              requestId: traceContext.requestId,
            });
            const events = await parseNDJSONStream<V1ThreadMessage>(
              reader,
              event => {
                // Reset the inactivity timer on every message so long-running streams
                // with periodic keepalives (e.g. astral's 15s heartbeats) are never
                // falsely killed before the server goes actually silent.
                messageCount++;
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                  didTimeout = true;
                  abortController.abort(STREAM_TIMEOUT_REASON);
                }, timeoutMs);
                optionsRef.current?.onMessage?.(event);
              },
              abortController.signal,
            );

            const totalDurationMs = Date.now() - requestStartTime;
            Sentry.logger.info(`${streamLogPrefix} stream complete`, {
              totalEvents: events.length,
              messagesReceived: messageCount,
              totalDurationMs,
              requestId: traceContext.requestId,
            });
            optionsRef.current?.onComplete?.(events);
            return events;
          } catch (err) {
            const streamTimeoutError = isStreamTimeoutError(
              err,
              didTimeout,
              abortController.signal,
            );
            const retryableError =
              streamTimeoutError || isRetryableStreamError(err);
            const hasMoreRetries = attempt < maxRetries;
            const isAborted =
              !streamTimeoutError &&
              (isStreamAbortError(err) || abortController.signal.aborted);

            Sentry.logger.debug(`${streamLogPrefix} catch`, {
              phase,
              attempt,
              maxRetries,
              messagesReceived: messageCount,
              requestId: traceContext.requestId,
              timeoutMs,
              isTimeout: streamTimeoutError,
              isRetryable: retryableError,
              isAborted,
              willRetry: retryableError && hasMoreRetries,
              error: err instanceof Error ? err.message : String(err),
              errorName: err instanceof Error ? err.name : undefined,
            });

            if (retryableError && hasMoreRetries) {
              const delayMs = getExponentialBackoffDelayMs(
                attempt,
                retryBaseDelayMs,
              );
              const reason = streamTimeoutError ? 'timeout' : 'retryable-error';
              optionsRef.current?.onRetry?.({
                attempt,
                maxRetries,
                delayMs,
                reason,
              });
              await waitForRetryDelay(delayMs);
              continue;
            }

            if (streamTimeoutError) {
              captureMessage('streaming_request_timeout', {
                level: 'debug',
                tags: {
                  service: 'zen',
                  operation: 'StreamThreadMessages',
                  phase,
                },
                extra: {
                  attempt,
                  maxRetries,
                  timeoutMs,
                  error:
                    err instanceof Error
                      ? {
                          message: err.message,
                          name: err.name,
                          stack: err.stack,
                        }
                      : err,
                },
              });
            }

            // Silently ignore abort errors - expected during cleanup/unmount.
            // abortController.signal.aborted covers Safari's TypeError("Load failed"),
            // which Safari throws for aborted fetches instead of the standard DOMException("AbortError").
            if (isAborted) {
              return [];
            }

            lastError = err as Error;
            break;
          } finally {
            if (timeoutId) clearTimeout(timeoutId);
          }
        }

        const streamError = lastError ?? new Error('Streaming request failed');
        throw streamError;
      } catch (err) {
        const streamError = err as Error;
        setError(streamError);
        optionsRef.current?.onError?.(streamError);
        throw streamError;
      } finally {
        if (longRequestTimer) {
          clearTimeout(longRequestTimer);
        }
        setIsLongRequest(false);
        setIsLoading(false);
      }
    },
    [getAccessTokenSilently, createAbortController],
  );

  return {
    streamThreadMessages,
    isLoading,
    isLongRequest,
    error,
    cancelCurrentStream,
  };
};

/**
 * @summary Resume streaming messages for the most recent user message in a thread.
Returns remaining messages for the last user message that haven't been
delivered yet. If processing is complete (no pending messages), the stream
closes immediately. This is useful for resuming after a client disconnect.
 * @description If `from_message_id` is set on the request, the server first replays every
persisted ThreadMessage with a `name` lexicographically greater than that
value (the catchup phase), then transitions to live tail.
 */
export const useStreamingResumeMessageStream = (
  options?: useStreamingResumeMessageStreamOptions,
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLongRequest, setIsLongRequest] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { getAccessTokenSilently } = useAuth();
  const { createAbortController, cancelCurrentStream } =
    useStreamingWithCleanup();
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const resumeMessageStream = useCallback(
    async (
      organization: string,
      workspace: string,
      automation: string,
      agent: string,
      thread: string,
      data: ThreadServiceResumeMessageStreamBody,
    ) => {
      setIsLoading(true);
      setIsLongRequest(false);
      setError(null);
      const streamLogPrefix = '[stream:zen/ResumeMessageStream]';
      const timeoutMs =
        optionsRef.current?.timeoutMs ?? DEFAULT_STREAM_TIMEOUT_MS;
      const maxRetries = Math.max(
        1,
        optionsRef.current?.maxRetries ?? DEFAULT_STREAM_MAX_RETRIES,
      );
      const retryBaseDelayMs =
        optionsRef.current?.retryBaseDelayMs ??
        DEFAULT_STREAM_RETRY_BASE_DELAY_MS;
      const longRequestThresholdMs =
        optionsRef.current?.longRequestThresholdMs ??
        DEFAULT_STREAM_LONG_REQUEST_THRESHOLD_MS;
      const longRequestTimer =
        longRequestThresholdMs > 0
          ? setTimeout(() => {
              setIsLongRequest(true);
              optionsRef.current?.onLongRequest?.({
                thresholdMs: longRequestThresholdMs,
              });
            }, longRequestThresholdMs)
          : null;

      try {
        let lastError: Error | null = null;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          const abortController = createAbortController();
          let didTimeout = false;
          // Track which phase the request is in for timeout diagnostics
          let phase: 'init' | 'request' | 'streaming' = 'init';
          let timeoutId: ReturnType<typeof setTimeout> | undefined;
          let messageCount = 0;
          const traceContext = getTraceContext();

          try {
            Sentry.logger.info(
              `${streamLogPrefix} attempt ${attempt}/${maxRetries}`,
              { timeoutMs, requestId: traceContext.requestId },
            );
            // Check for E2E test mode (SSR-safe: checks build-time VITE_E2E_TEST_MODE first,
            // then runtime window.__ENV__ if available)
            const isE2EMode = isE2ETestMode();
            const headers: Record<string, string> = {
              'Content-Type': 'application/json',
              Accept: 'application/x-ndjson',
            };
            if (isE2EMode) {
              headers['X-E2E-Test-Mode'] = 'true';
            } else {
              const tokenStart = Date.now();
              const token = await getAccessTokenSilently();
              const tokenDurationMs = Date.now() - tokenStart;
              if (tokenDurationMs > SLOW_TOKEN_THRESHOLD_MS) {
                captureMessage('slow_token_acquisition', {
                  level: 'warning',
                  tags: {
                    service: 'zen',
                    operation: 'ResumeMessageStream',
                  },
                  extra: { durationMs: tokenDurationMs },
                });
              }
              headers.Authorization = `Bearer ${token}`;
            }

            headers['x-kog-request-id'] = traceContext.requestId;

            // Start inactivity timeout AFTER token acquisition so auth latency
            // does not eat into the server response budget.
            // Rolling timeout: reset on every received message (including heartbeats).
            // Only fires if the server goes completely silent for timeoutMs.
            phase = 'request';
            const requestStartTime = Date.now();
            Sentry.logger.info(`${streamLogPrefix} sending request`, {
              url: `/api/v1/organizations/${organization}/workspaces/${workspace}/automations/${automation}/agents/${agent}/threads/${thread}:resumeStream`,
              requestId: traceContext.requestId,
            });
            timeoutId = setTimeout(() => {
              didTimeout = true;
              abortController.abort(STREAM_TIMEOUT_REASON);
            }, timeoutMs);

            const response = await fetch(
              `/api/v1/organizations/${organization}/workspaces/${workspace}/automations/${automation}/agents/${agent}/threads/${thread}:resumeStream`,
              {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
                signal: abortController.signal,
              },
            );

            const responseLatencyMs = Date.now() - requestStartTime;
            Sentry.logger.info(`${streamLogPrefix} response received`, {
              status: response.status,
              latencyMs: responseLatencyMs,
              requestId: traceContext.requestId,
            });

            if (!response.ok) {
              const httpError = new Error(
                `HTTP error! status: ${response.status}`,
              );
              (httpError as Error & { status: number }).status =
                response.status;
              throw httpError;
            }

            const reader = response.body?.getReader();
            if (!reader) throw new Error('No response body');

            phase = 'streaming';
            Sentry.logger.info(`${streamLogPrefix} streaming started`, {
              requestId: traceContext.requestId,
            });
            const events = await parseNDJSONStream<V1ThreadMessage>(
              reader,
              event => {
                // Reset the inactivity timer on every message so long-running streams
                // with periodic keepalives (e.g. astral's 15s heartbeats) are never
                // falsely killed before the server goes actually silent.
                messageCount++;
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                  didTimeout = true;
                  abortController.abort(STREAM_TIMEOUT_REASON);
                }, timeoutMs);
                optionsRef.current?.onMessage?.(event);
              },
              abortController.signal,
            );

            const totalDurationMs = Date.now() - requestStartTime;
            Sentry.logger.info(`${streamLogPrefix} stream complete`, {
              totalEvents: events.length,
              messagesReceived: messageCount,
              totalDurationMs,
              requestId: traceContext.requestId,
            });
            optionsRef.current?.onComplete?.(events);
            return events;
          } catch (err) {
            const streamTimeoutError = isStreamTimeoutError(
              err,
              didTimeout,
              abortController.signal,
            );
            const retryableError =
              streamTimeoutError || isRetryableStreamError(err);
            const hasMoreRetries = attempt < maxRetries;
            const isAborted =
              !streamTimeoutError &&
              (isStreamAbortError(err) || abortController.signal.aborted);

            Sentry.logger.debug(`${streamLogPrefix} catch`, {
              phase,
              attempt,
              maxRetries,
              messagesReceived: messageCount,
              requestId: traceContext.requestId,
              timeoutMs,
              isTimeout: streamTimeoutError,
              isRetryable: retryableError,
              isAborted,
              willRetry: retryableError && hasMoreRetries,
              error: err instanceof Error ? err.message : String(err),
              errorName: err instanceof Error ? err.name : undefined,
            });

            if (retryableError && hasMoreRetries) {
              const delayMs = getExponentialBackoffDelayMs(
                attempt,
                retryBaseDelayMs,
              );
              const reason = streamTimeoutError ? 'timeout' : 'retryable-error';
              optionsRef.current?.onRetry?.({
                attempt,
                maxRetries,
                delayMs,
                reason,
              });
              await waitForRetryDelay(delayMs);
              continue;
            }

            if (streamTimeoutError) {
              captureMessage('streaming_request_timeout', {
                level: 'debug',
                tags: {
                  service: 'zen',
                  operation: 'ResumeMessageStream',
                  phase,
                },
                extra: {
                  attempt,
                  maxRetries,
                  timeoutMs,
                  error:
                    err instanceof Error
                      ? {
                          message: err.message,
                          name: err.name,
                          stack: err.stack,
                        }
                      : err,
                },
              });
            }

            // Silently ignore abort errors - expected during cleanup/unmount.
            // abortController.signal.aborted covers Safari's TypeError("Load failed"),
            // which Safari throws for aborted fetches instead of the standard DOMException("AbortError").
            if (isAborted) {
              return [];
            }

            lastError = err as Error;
            break;
          } finally {
            if (timeoutId) clearTimeout(timeoutId);
          }
        }

        const streamError = lastError ?? new Error('Streaming request failed');
        throw streamError;
      } catch (err) {
        const streamError = err as Error;
        setError(streamError);
        optionsRef.current?.onError?.(streamError);
        throw streamError;
      } finally {
        if (longRequestTimer) {
          clearTimeout(longRequestTimer);
        }
        setIsLongRequest(false);
        setIsLoading(false);
      }
    },
    [getAccessTokenSilently, createAbortController],
  );

  return {
    resumeMessageStream,
    isLoading,
    isLongRequest,
    error,
    cancelCurrentStream,
  };
};

/**
 * @summary Send a new user message to a thread and stream the agent's response.
The stream includes the created user message, followed by all agent-generated
messages (progress notifications, artifacts, tool calls, agent messages, etc.)
until a CompletionResponse is received. The stream closes after the
CompletionResponse is sent.
 */
export const useStreamingSendMessage = (
  options?: useStreamingSendMessageOptions,
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLongRequest, setIsLongRequest] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { getAccessTokenSilently } = useAuth();
  const { createAbortController, cancelCurrentStream } =
    useStreamingWithCleanup();
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const sendMessage = useCallback(
    async (
      organization: string,
      workspace: string,
      automation: string,
      agent: string,
      thread: string,
      data: ThreadServiceSendMessageBody,
    ) => {
      setIsLoading(true);
      setIsLongRequest(false);
      setError(null);
      const streamLogPrefix = '[stream:zen/SendMessage]';
      const timeoutMs =
        optionsRef.current?.timeoutMs ?? DEFAULT_STREAM_TIMEOUT_MS;
      const maxRetries = Math.max(
        1,
        optionsRef.current?.maxRetries ?? DEFAULT_STREAM_MAX_RETRIES,
      );
      const retryBaseDelayMs =
        optionsRef.current?.retryBaseDelayMs ??
        DEFAULT_STREAM_RETRY_BASE_DELAY_MS;
      const longRequestThresholdMs =
        optionsRef.current?.longRequestThresholdMs ??
        DEFAULT_STREAM_LONG_REQUEST_THRESHOLD_MS;
      const longRequestTimer =
        longRequestThresholdMs > 0
          ? setTimeout(() => {
              setIsLongRequest(true);
              optionsRef.current?.onLongRequest?.({
                thresholdMs: longRequestThresholdMs,
              });
            }, longRequestThresholdMs)
          : null;

      try {
        let lastError: Error | null = null;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          const abortController = createAbortController();
          let didTimeout = false;
          // Track which phase the request is in for timeout diagnostics
          let phase: 'init' | 'request' | 'streaming' = 'init';
          let timeoutId: ReturnType<typeof setTimeout> | undefined;
          let messageCount = 0;
          const traceContext = getTraceContext();

          try {
            Sentry.logger.info(
              `${streamLogPrefix} attempt ${attempt}/${maxRetries}`,
              { timeoutMs, requestId: traceContext.requestId },
            );
            // Check for E2E test mode (SSR-safe: checks build-time VITE_E2E_TEST_MODE first,
            // then runtime window.__ENV__ if available)
            const isE2EMode = isE2ETestMode();
            const headers: Record<string, string> = {
              'Content-Type': 'application/json',
              Accept: 'application/x-ndjson',
            };
            if (isE2EMode) {
              headers['X-E2E-Test-Mode'] = 'true';
            } else {
              const tokenStart = Date.now();
              const token = await getAccessTokenSilently();
              const tokenDurationMs = Date.now() - tokenStart;
              if (tokenDurationMs > SLOW_TOKEN_THRESHOLD_MS) {
                captureMessage('slow_token_acquisition', {
                  level: 'warning',
                  tags: {
                    service: 'zen',
                    operation: 'SendMessage',
                  },
                  extra: { durationMs: tokenDurationMs },
                });
              }
              headers.Authorization = `Bearer ${token}`;
            }

            headers['x-kog-request-id'] = traceContext.requestId;

            // Start inactivity timeout AFTER token acquisition so auth latency
            // does not eat into the server response budget.
            // Rolling timeout: reset on every received message (including heartbeats).
            // Only fires if the server goes completely silent for timeoutMs.
            phase = 'request';
            const requestStartTime = Date.now();
            Sentry.logger.info(`${streamLogPrefix} sending request`, {
              url: `/api/v1/organizations/${organization}/workspaces/${workspace}/automations/${automation}/agents/${agent}/threads/${thread}:sendMessage`,
              requestId: traceContext.requestId,
            });
            timeoutId = setTimeout(() => {
              didTimeout = true;
              abortController.abort(STREAM_TIMEOUT_REASON);
            }, timeoutMs);

            const response = await fetch(
              `/api/v1/organizations/${organization}/workspaces/${workspace}/automations/${automation}/agents/${agent}/threads/${thread}:sendMessage`,
              {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
                signal: abortController.signal,
              },
            );

            const responseLatencyMs = Date.now() - requestStartTime;
            Sentry.logger.info(`${streamLogPrefix} response received`, {
              status: response.status,
              latencyMs: responseLatencyMs,
              requestId: traceContext.requestId,
            });

            if (!response.ok) {
              const httpError = new Error(
                `HTTP error! status: ${response.status}`,
              );
              (httpError as Error & { status: number }).status =
                response.status;
              throw httpError;
            }

            const reader = response.body?.getReader();
            if (!reader) throw new Error('No response body');

            phase = 'streaming';
            Sentry.logger.info(`${streamLogPrefix} streaming started`, {
              requestId: traceContext.requestId,
            });
            const events = await parseNDJSONStream<V1ThreadMessage>(
              reader,
              event => {
                // Reset the inactivity timer on every message so long-running streams
                // with periodic keepalives (e.g. astral's 15s heartbeats) are never
                // falsely killed before the server goes actually silent.
                messageCount++;
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                  didTimeout = true;
                  abortController.abort(STREAM_TIMEOUT_REASON);
                }, timeoutMs);
                optionsRef.current?.onMessage?.(event);
              },
              abortController.signal,
            );

            const totalDurationMs = Date.now() - requestStartTime;
            Sentry.logger.info(`${streamLogPrefix} stream complete`, {
              totalEvents: events.length,
              messagesReceived: messageCount,
              totalDurationMs,
              requestId: traceContext.requestId,
            });
            optionsRef.current?.onComplete?.(events);
            return events;
          } catch (err) {
            const streamTimeoutError = isStreamTimeoutError(
              err,
              didTimeout,
              abortController.signal,
            );
            const retryableError =
              streamTimeoutError || isRetryableStreamError(err);
            const hasMoreRetries = attempt < maxRetries;
            const isAborted =
              !streamTimeoutError &&
              (isStreamAbortError(err) || abortController.signal.aborted);

            Sentry.logger.debug(`${streamLogPrefix} catch`, {
              phase,
              attempt,
              maxRetries,
              messagesReceived: messageCount,
              requestId: traceContext.requestId,
              timeoutMs,
              isTimeout: streamTimeoutError,
              isRetryable: retryableError,
              isAborted,
              willRetry: retryableError && hasMoreRetries,
              error: err instanceof Error ? err.message : String(err),
              errorName: err instanceof Error ? err.name : undefined,
            });

            if (retryableError && hasMoreRetries) {
              const delayMs = getExponentialBackoffDelayMs(
                attempt,
                retryBaseDelayMs,
              );
              const reason = streamTimeoutError ? 'timeout' : 'retryable-error';
              optionsRef.current?.onRetry?.({
                attempt,
                maxRetries,
                delayMs,
                reason,
              });
              await waitForRetryDelay(delayMs);
              continue;
            }

            if (streamTimeoutError) {
              captureMessage('streaming_request_timeout', {
                level: 'debug',
                tags: {
                  service: 'zen',
                  operation: 'SendMessage',
                  phase,
                },
                extra: {
                  attempt,
                  maxRetries,
                  timeoutMs,
                  error:
                    err instanceof Error
                      ? {
                          message: err.message,
                          name: err.name,
                          stack: err.stack,
                        }
                      : err,
                },
              });
            }

            // Silently ignore abort errors - expected during cleanup/unmount.
            // abortController.signal.aborted covers Safari's TypeError("Load failed"),
            // which Safari throws for aborted fetches instead of the standard DOMException("AbortError").
            if (isAborted) {
              return [];
            }

            lastError = err as Error;
            break;
          } finally {
            if (timeoutId) clearTimeout(timeoutId);
          }
        }

        const streamError = lastError ?? new Error('Streaming request failed');
        throw streamError;
      } catch (err) {
        const streamError = err as Error;
        setError(streamError);
        optionsRef.current?.onError?.(streamError);
        throw streamError;
      } finally {
        if (longRequestTimer) {
          clearTimeout(longRequestTimer);
        }
        setIsLongRequest(false);
        setIsLoading(false);
      }
    },
    [getAccessTokenSilently, createAbortController],
  );

  return {
    sendMessage,
    isLoading,
    isLongRequest,
    error,
    cancelCurrentStream,
  };
};
